import { useEffect, useRef, useState } from 'react';
import './ChatInput.css';

const PLACEHOLDERS = {
  simple: 'Message Piua AI…',
  docs: 'Ask a question about the attached document…',
  image: 'Describe the image you want to generate…',
};

export default function ChatInput({
  mode,
  onSend,
  onStop,
  disabled,
  isStreaming,
  attachedDoc,
  attaching,
  attachError,
  onAttach,
  onRemoveAttachment,
}) {
  const [value, setValue] = useState('');
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [value]);

  function submit() {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onAttach(file);
  }

  return (
    <div className="chat-input-wrap">
      {mode === 'docs' && (attachedDoc || attaching || attachError) && (
        <div className="chat-attachment">
          {attaching ? (
            <span>Reading document…</span>
          ) : attachError ? (
            <span className="chat-attachment-error">{attachError}</span>
          ) : (
            <>
              <span className="chat-attachment-name">{attachedDoc.filename}</span>
              <button type="button" onClick={onRemoveAttachment} title="Remove document">
                ×
              </button>
            </>
          )}
        </div>
      )}
      <div className="chat-input-box">
        {mode === 'docs' && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,.md"
              hidden
              onChange={handleFileChange}
            />
            <button
              type="button"
              className="attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || attaching}
              title="Attach a document"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M21 11L12.5 19.5C10.6 21.4 7.4 21.4 5.5 19.5C3.6 17.6 3.6 14.4 5.5 12.5L14 4C15.3 2.7 17.4 2.7 18.7 4C20 5.3 20 7.4 18.7 8.7L10.5 16.9C9.8 17.6 8.7 17.6 8 16.9C7.3 16.2 7.3 15.1 8 14.4L15.5 7"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          placeholder={PLACEHOLDERS[mode] || PLACEHOLDERS.simple}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        {isStreaming ? (
          <button className="send-btn send-btn--stop" onClick={onStop} title="Stop generating">
            <span className="stop-icon" />
          </button>
        ) : (
          <button
            className="send-btn"
            onClick={submit}
            disabled={disabled || !value.trim()}
            title="Send"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 12L20 4L13 20L11 13L4 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>
      <div className="chat-input-hint">Piua AI can make mistakes. Verify important information.</div>
    </div>
  );
}
